import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { FileText, Plus, Search, Eye, Edit, Send, Filter, ChevronRight } from 'lucide-react';

function EstadoBadge({ estado }) {
  const map = {
    firmado: { label: 'Firmado', cls: 'badge-firmado' },
    pendiente_firma: { label: 'Pdte. firma', cls: 'badge-pendiente_firma' },
    borrador: { label: 'Borrador', cls: 'badge-borrador' },
    rechazado: { label: 'Rechazado', cls: 'badge-rechazado' },
    archivado: { label: 'Archivado', cls: 'badge-archivado' },
  };
  const m = map[estado] || { label: estado, cls: '' };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}

export default function PntsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canEdit } = useAuth();
  const [pnts, setPnts] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState(searchParams.get('estado') || '');
  const [catId, setCatId] = useState('');

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (estado) params.estado = estado;
    if (catId) params.categoria_id = catId;
    setLoading(true);
    api.get('/pnts', { params }).then(r => setPnts(r.data.pnts || [])).finally(() => setLoading(false));
  }, [q, estado, catId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/pnts/categorias').then(r => setCategorias(r.data)); }, []);

  const handleSolicitarFirma = async (e, pntId) => {
    e.stopPropagation();
    try {
      await api.post('/firmas/solicitar', { pnt_id: pntId });
      load();
      alert('Solicitud de firma enviada correctamente');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al solicitar firma');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1>PNTs y Procedimientos</h1>
          <p>{pnts.length} documentos</p>
        </div>
        {canEdit() && (
          <button className="btn btn-primary" onClick={() => navigate('/pnts/nuevo')}>
            <Plus size={16} /> Nuevo PNT
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-wrap">
            <Search size={15} />
            <input
              className="search-input"
              placeholder="Buscar por título o código..."
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{ width: 280 }}
            />
          </div>
          <select className="form-control" style={{ width: 180 }} value={estado} onChange={e => setEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="pendiente_firma">Pendiente de firma</option>
            <option value="firmado">Firmado</option>
            <option value="rechazado">Rechazado</option>
            <option value="archivado">Archivado</option>
          </select>
          <select className="form-control" style={{ width: 220 }} value={catId} onChange={e => setCatId(e.target.value)}>
            <option value="">Todas las categorías</option>
            {categorias.map(c => (
              <option key={c.id} value={c.id}>{c.codigo} - {c.nombre}</option>
            ))}
          </select>
          {(q || estado || catId) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setQ(''); setEstado(''); setCatId(''); }}>
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        {loading ? (
          <div className="loading-overlay"><div className="spinner" /><span>Cargando...</span></div>
        ) : pnts.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No hay documentos</h3>
            <p>No se encontraron PNTs con los filtros aplicados</p>
            {canEdit() && (
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/pnts/nuevo')}>
                <Plus size={16} /> Crear primer PNT
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Título</th>
                  <th>Categoría</th>
                  <th>Versión</th>
                  <th>Responsable</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pnts.map(pnt => (
                  <tr key={pnt.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pnts/${pnt.id}`)}>
                    <td>
                      <span style={{ 
                        fontFamily: 'var(--font-mono)', fontSize: '0.78rem', 
                        fontWeight: 700, color: pnt.categoria_color || 'var(--color-primary)',
                        background: `${pnt.categoria_color}18` || 'var(--color-primary-light)',
                        padding: '2px 8px', borderRadius: 4
                      }}>
                        {pnt.codigo}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500, maxWidth: 260 }}>
                      <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {pnt.titulo}
                      </div>
                      {pnt.num_archivos > 0 && (
                        <div style={{ fontSize: '0.73rem', color: 'var(--color-text-3)', marginTop: 2 }}>
                          📎 {pnt.num_archivos} archivo(s)
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: '0.8rem', color: pnt.categoria_color, fontWeight: 600 }}>
                        {pnt.categoria_nombre}
                      </span>
                    </td>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>v{pnt.version}</span></td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-2)' }}>
                      {pnt.responsable_nombre ? `${pnt.responsable_nombre} ${pnt.responsable_apellidos || ''}` : '—'}
                    </td>
                    <td><EstadoBadge estado={pnt.estado} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--color-text-3)' }}>
                      {pnt.fecha_creacion ? new Date(pnt.fecha_creacion).toLocaleDateString('es-ES') : '—'}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Ver" onClick={() => navigate(`/pnts/${pnt.id}`)}>
                          <Eye size={14} />
                        </button>
                        {pnt.estado === 'borrador' && canEdit() && (
                          <button className="btn btn-ghost btn-icon btn-sm" title="Solicitar firma" 
                            onClick={e => handleSolicitarFirma(e, pnt.id)}
                            style={{ color: 'var(--color-warning)' }}>
                            <Send size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
